// The debrief room's decision panels (lane B7, S15): his words beyond the shared ones, the lane's closed vocabulary
// (backend/app/evaluation/vocab.py, one spelling) and the reads of the room's view the panels share. Every label quotes
// GLOSSARY.md or the DS excerpts verbatim; nothing here is pending, because none of these words waits on a question.
import { defineLabels } from "@/mc/labels";
import type { DebriefOpenOutput, MemberOut, RoomExtras } from "@/api/tools";

const REVISE = "\"Sr Director and below: interviewer may revise only own rating/recommendation with rationale/audit; final unanimous Hire required.\"";
const EXCEPTION = "\"Additional interview requires unassessed competency, reason, owner and HM/Recruiter agreement.\"";
const CORRECTION = "| Administrative correction | \"VP+ No Hire rule cannot be overridden except administrative correction.\" \"Admin may correct workflow metadata but not substantive score.\"";
const AUDIT = "\"Audit is append-only: old/new, actor, timestamp, reason, related req/candidate, evidence and source.\"";

/** The rule sentences the panels show where a control is blocked or refused (his, verbatim). */
export const DECIDE_RULES = /* @__PURE__ */ defineLabels({
  reviseRule: { text: "Sr Director and below: interviewer may revise only own rating/recommendation with rationale/audit; final unanimous Hire required." 


},
  vpStrict: { text: "VP+: strict No Hire rule." },
  revisionPreserves: { text: "revision preserves original, actor, time, rationale." 


},
  exceptionRule: { text: "Additional interview requires unassessed competency, reason, owner and HM/Recruiter agreement." 


},
  correctionRule: { text: "Admin may correct workflow metadata but not substantive score." },
  vpCorrection: { text: "VP+ No Hire rule cannot be overridden except administrative correction." },
  missingScorecardKeepsOpen: { text: "Do not finalize with missing scorecards." 


},
  alternatesActive: { text: "Alternates stay active until primary accepts." 


},
});

/** The panels' nouns: the gate's rows, the exception request's fields, the correction's fields. Owner, Rationale,
 * Timestamp, Source, Evidence and Withdrawn are the design system's words, drawn from @/mc where they are used. */
export const DECIDE_LABELS = /* @__PURE__ */ defineLabels({
  missingScorecard: { text: "Missing scorecard" 


},
  hmRecruiterAgreement: { text: "HM/Recruiter agreement" },
  unassessedCompetency: { text: "Unassessed competency" 

},
  administrativeCorrection: { text: "Administrative correction" },
});

// ---- the lane's closed words (vocab.py; one spelling) ---------------------------------------------------------------

export const BANDS = ["below_vp", "vp_plus", "legacy"] as const;
export type Band = (typeof BANDS)[number];

/** His outcome words in the room, in his order. */
export const HIS_OUTCOMES = ["primary", "alternates", "continue", "reopen", "exception_round", "others"] as const;
export type HisOutcome = (typeof HIS_OUTCOMES)[number];

/** The decision record each word writes; reopen records a search outcome instead (debrief.record_search_outcome). */
export type DecisionValue = "extend_offer" | "hold_as_alternate" | "continue" | "additional_round" | "pass";
export const DECISION_OF: Readonly<Record<HisOutcome, DecisionValue | null>> = {
  primary: "extend_offer",
  alternates: "hold_as_alternate",
  continue: "continue",
  exception_round: "additional_round",
  others: "pass",
  reopen: null,
};

export const CORRECTION_FIELDS = ["effective_at", "source", "source_file_id", "recorded_in_error", "withdrawn_from_evidence"] as const;
export type CorrectionField = (typeof CORRECTION_FIELDS)[number];
export const WAIVER_KINDS = ["waived", "lifted"] as const;
export const COMPETENCY_TYPES = ["value", "skill"] as const;
export type CompetencyType = (typeof COMPETENCY_TYPES)[number];
export const AIR_STATUSES = ["requested", "agreed", "withdrawn", "used"] as const;
export type AirStatus = (typeof AIR_STATUSES)[number];

/** A reason's bounds (D7, D8): 3–2000 characters. */
export const REASON_MIN = 3;
export const REASON_MAX = 2000;
export const reasonOk = (text: string) => text.trim().length >= REASON_MIN && text.trim().length <= REASON_MAX;

// ---- the room's view: `room`, `blocked_reasons` and `room_status` are the generated CycleOut's ---------------------------

export const roomOf = (view: DebriefOpenOutput): RoomExtras | null => view.room ?? null;

export const memberOf = (view: DebriefOpenOutput, applicationId: string): MemberOut | undefined =>
  view.members.find((m) => m.application_id === applicationId);

/** The band the room decides under; unknown reads as legacy, which never offers a revision. */
export function bandOf(view: DebriefOpenOutput): Band {
  const band = view.room?.band ?? null;
  return band && (BANDS as readonly string[]).includes(band) ? band : "legacy";
}

/** A capability flag the server computed for the reader (absent: false). */
export const can = (flags: Record<string, boolean> | null | undefined, key: string) => Boolean(flags?.[key]);

/** The candidate's display words on a member row (the server's projection decides what it carries). */
export const candidateName = (member: MemberOut | undefined, fallback: string): string => member?.candidate || fallback;

/** The participant id the room marks as the reader's own (an alignment row with `mine`). */
export function myParticipantId(view: DebriefOpenOutput): string | null {
  for (const m of view.members) {
    const row = m.alignments.find((a) => a.mine === true);
    if (row && typeof row.participant_id === "string") return row.participant_id;
  }
  return null;
}

/** A required attendee of a role (the roll's own entries: person_id, name, role, status). */
export interface Attendee {
  person_id: string;
  name: string;
  role: string;
  status: string | null;
}
export const attendees = (view: DebriefOpenOutput): Attendee[] =>
  view.required_attendees.map((a) => ({
    person_id: String(a.person_id ?? ""),
    name: String(a.name ?? ""),
    role: String(a.role ?? ""),
    status: typeof a.status === "string" ? a.status : null,
  }));

/** The query prefixes a room write invalidates: the room's own read (["debrief", cycle, requisition]). */
export const ROOM_KEYS: readonly (readonly unknown[])[] = [["debrief"]];
